import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import { apiRequest } from '../utils/api';

const TradierMcpTest = () => {
  const [apiToken, setApiToken] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [paperTrading, setPaperTrading] = useState(false);
  const [selectedTool, setSelectedTool] = useState('get_positions');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [rawRequest, setRawRequest] = useState(null);
  const [availableTools, setAvailableTools] = useState(null);
  const [accountId, setAccountId] = useState('');

  const defaultTools = [
    { name: 'get_positions', label: 'Get Positions' },
    { name: 'get_account_history', label: 'Get Account History' },
    { name: 'get_quote', label: 'Get Quote' },
    { name: 'get_option_chain', label: 'Get Option Chain' },
  ];

  const handleTest = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setRawRequest(null);

    try {
      const payload = {
        apiToken,
        accountNumber,
        paperTrading,
        tool: selectedTool
      };

      setRawRequest(payload);

      const response = await apiRequest('/api/mcp/test-tradier', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchTools = async () => {
    setLoading(true);
    setError(null);
    setAvailableTools(null);

    try {
      const response = await apiRequest(`/api/mcp/tradier/tools?apiToken=${encodeURIComponent(apiToken)}&paperTrading=${paperTrading}`);
      setAvailableTools(response.data.tools);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchAccountDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiRequest(`/api/accounts/${accountId}`);
      const account = response.data;
      
      // Set the account number and token from the database
      if (account.api_account_id) {
        setAccountNumber(account.api_account_id);
      }
      if (account.api_token) {
        setApiToken(account.api_token);
      }
      if (account.environment === 'paper') {
        setPaperTrading(true);
      } else {
        setPaperTrading(false);
      }
      
      setResult({ message: 'Account details loaded successfully', account: { name: account.name, apiType: account.api_type, environment: account.environment } });
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTestClosedPositions = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setRawRequest(null);

    try {
      const payload = { accountId };
      setRawRequest(payload);

      const response = await apiRequest(`/api/mcp/tradier/closed-positions?accountId=${accountId}`);
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>


      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Configuration
          </Typography>

          <TextField
            fullWidth
            label="API Token"
            type="password"
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Account Number"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Account ID (for Closed Positions)"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            sx={{ mb: 2 }}
            helperText="Database account ID for testing closed positions endpoint"
          />

          <Button
            variant="outlined"
            onClick={handleFetchAccountDetails}
            disabled={loading || !accountId}
            fullWidth
            sx={{ mb: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Load Account Details from DB'}
          </Button>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Trading Mode</InputLabel>
            <Select
              value={paperTrading}
              label="Trading Mode"
              onChange={(e) => setPaperTrading(e.target.value)}
            >
              <MenuItem value={false}>Live Trading</MenuItem>
              <MenuItem value={true}>Paper Trading</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Tool</InputLabel>
            <Select
              value={selectedTool}
              label="Select Tool"
              onChange={(e) => setSelectedTool(e.target.value)}
            >
              {(Array.isArray(availableTools) ? availableTools : defaultTools).map((tool) => (
                <MenuItem key={tool.name || tool} value={tool.name || tool}>
                  {tool.name || tool}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="contained"
              onClick={handleTest}
              disabled={loading || !apiToken}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : 'Test Tool'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleFetchTools}
              disabled={loading}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : 'Fetch Tools'}
            </Button>
          </Box>

          <Button
            variant="contained"
            color="secondary"
            onClick={handleTestClosedPositions}
            disabled={loading || !accountId}
            fullWidth
            sx={{ mb: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Test Closed Positions'}
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {rawRequest && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Raw Request
            </Typography>
            <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(rawRequest, null, 2)}
              </pre>
            </Paper>
          </CardContent>
        </Card>
      )}

      {availableTools && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Available Tools
            </Typography>
            <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(availableTools, null, 2)}
              </pre>
            </Paper>
          </CardContent>
        </Card>
      )}

      {result && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Result
            </Typography>
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(result, null, 2)}
              </pre>
            </Paper>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default TradierMcpTest;
